/** venture/vics13.ts — 13단계 「VICS 투자실적 보고」 정의 (조합이 인수한 전환사채 등록 · 투자확인서 발급 전 단계, 2026-09-24)
 *  VICS 화면 순서 [1313 투자기업 등록] → [1319 투자유형 관리] → [1320 투자거래 관리] → [1329 보고 검증·확정] 대로
 *  옮겨 적을 값을 표로 보여준다. 아는 값은 앞단계(등록정보·8단계 사채정보)에서 자동, 모르는 값은 직접 입력(s13 txt).
 *  첨부 서류(등기부등본·사업자등록증·벤처기업확인서·입금내역)는 앞단계 업로드를 그대로 열람·다운로드한다. */
import { getTxt, getUp, getUpKey, getChk, getStore, mutStore } from './stepcommon';
import { bondVal } from './bondcalc';
import { coVal } from './collect';
import { regCoOf, won, repPerson, birthFromRrn } from './coterms';
import { kitKey } from './certplan';
import type { Project, CoCard, Co, StoreKey, TmCoInfo } from './types';

export const VICS_URL = 'http://install.k-vic.co.kr';
export type VicsStageKey = '1313' | '1319' | '1320' | '1329';
export type VicsDocKey = 'regcb' | 'biz' | 'vcert' | 'fundout';
type AutoFn = (p: Project, co: CoCard, rc: Co | null) => string;
export interface VicsField {
  slot: string; label: string; kind?: 'text' | 'date' | 'num';   // num: 화면엔 천단위 콤마, 저장·복사는 숫자만
  auto?: AutoFn; autoLabel?: string;   // 앞단계 연동값 (직접 입력값이 있으면 그것이 우선)
  hint?: string; doc?: VicsDocKey;     // doc: 값을 읽어 올 서류 (열기 버튼)
}
/** scope — co: 법인(투자 건)마다 · fund: 조합 단위로 한 번 ([1329] 보고 검증·확정은 조합의 전송 버튼, 법인별이 아니다) */
export interface VicsStage { key: VicsStageKey; title: string; note: string; fields: VicsField[]; docs: VicsDocKey[]; scope: 'co' | 'fund'; }

const amtOf: AutoFn = (_p, _co, rc) => (rc && rc.amt) ? String(won(rc.amt)) : '';
const sharesOf: AutoFn = (p, co, rc) => {
  const amt = rc ? won(rc.amt) : 0; const price = won(bondVal(p, co, 'convPrice'));
  return amt > 0 && price > 0 ? String(Math.floor(amt / price)) : '';
};
const issueOf: AutoFn = (p, co) => getTxt(p, 's8', co.id + '_b_issueDate');
const REG = '8단계 사채정보'; const CO = '등록정보'; const DB = '업체DB';
/** 업무관리 업체 정보 스냅샷(p.tmco) — 13·14단계가 열릴 때 채운다 (kithooks.useTmCompanies) */
const tmOf = (p: Project, co: CoCard): TmCoInfo | undefined => (co.companyId && p.tmco ? p.tmco[co.companyId] : undefined);
const tm = (f: keyof TmCoInfo, date?: boolean): AutoFn => (p, co) => { const t = tmOf(p, co); const v = t ? t[f] : ''; return v ? (date ? String(v).slice(0, 10) : String(v)) : ''; };
const first = (...fns: AutoFn[]): AutoFn => (p, co, rc) => { for (let i = 0; i < fns.length; i++) { const v = fns[i](p, co, rc); if (v) return v; } return ''; };
const repBirth: AutoFn = (p, co) => { const x = repPerson(p, co); return x ? birthFromRrn(x.rrn) : ''; };

export const VICS_STAGES: VicsStage[] = [
  // 고정 선택값(국가 한국 · 인증기업구분 벤처기업 · 벤처유형 벤처투자유형 · 결산월 12 · 자산건전성 정상 등)은 행으로 늘어놓지 않고 안내 한 줄로 둔다(지도사 확정 2026-09-24)
  { key: '1313', title: '투자기업 등록', note: '「입력」 → 아래 값 입력 → 첨부파일에 사업자등록증·법인등기부등본(벤처기업확인서) 추가 → 「저장」 · 고정 선택: 국가 한국 · 인증기업구분 벤처기업 · 벤처유형 벤처투자유형 · 결산월 12월 · 자산건전성 정상 · 4차산업/비대면 해당없음', scope: 'co', docs: ['biz', 'regcb', 'vcert'], fields: [
    { slot: 'corp', label: '투자기업명', auto: (_p, co) => co.name, autoLabel: CO },
    { slot: 'crno', label: '법인등록번호', auto: tm('company_reg_num'), autoLabel: DB, doc: 'regcb', hint: '등기부등본 상단 (000000-0000000)' },
    { slot: 'bizno', label: '사업자등록번호', auto: (_p, co) => co.bizno, autoLabel: CO, doc: 'biz' },
    { slot: 'founded', label: '설립일 (등기부등본상 회사성립연월일)', kind: 'date', auto: tm('established_date', true), autoLabel: DB, doc: 'regcb' },
    { slot: 'vno', label: '벤처확인번호', doc: 'vcert', hint: '벤처기업확인서의 확인번호' },
    { slot: 'vfrom', label: '벤처 시작일 (유효기간 시작)', kind: 'date', doc: 'vcert' },
    { slot: 'vto', label: '벤처 종료일 (유효기간 종료)', kind: 'date', doc: 'vcert' },
    { slot: 'biztype', label: '업종 (대표 업종)', auto: tm('biz_type'), autoLabel: DB, doc: 'biz', hint: '사업자등록증 업태·종목' },
    { slot: 'addr', label: '주소 (등기부등본상 본점 소재지)', auto: first((p, co) => bondVal(p, co, 'issuerAddr'), tm('address')), autoLabel: REG + '/' + DB, doc: 'regcb' },
    { slot: 'ceoBirth', label: '대표자 생년월일', kind: 'date', auto: first(tm('ceo_birth', true), repBirth), autoLabel: DB + '/마스터', doc: 'regcb', hint: '등기부등본 대표이사 주민번호 앞자리' },
    { slot: 'tel', label: '대표전화번호', auto: first(tm('ceo_phone'), (_p, _co, rc) => (rc && rc.mgrTel) || ''), autoLabel: DB + '/' + CO },
    { slot: 'mgr', label: '투자기업 담당자명', auto: first(tm('manager'), (_p, _co, rc) => (rc && rc.ceo) || ''), autoLabel: DB + '/' + CO },
    { slot: 'mgrTel', label: '담당자 전화번호', auto: first(tm('manager_phone'), (_p, _co, rc) => (rc && rc.mgrTel) || ''), autoLabel: DB + '/' + CO },
    { slot: 'mgrEmail', label: '담당자 이메일', auto: first(tm('manager_email'), tm('ceo_email')), autoLabel: DB },
  ] },
  { key: '1319', title: '투자유형 관리', note: '「입력」 → 재원(조합)·투자기업(1313 등록분) 선택 → 아래 값 입력 → 「저장」 · 고정 선택: 투자유형 전환사채(CB) · 차수 1 · 신주 · 실적구분 의무투자 · 프로젝트명 해당없음 · 관리보수/회수/주목적 N', scope: 'co', docs: ['regcb'], fields: [
    { slot: 'contractDate', label: '투자계약일자 (등기부등본상 사채 발행일자)', kind: 'date', auto: issueOf, autoLabel: REG, doc: 'regcb' },
    { slot: 'amount', label: '투자계약금액 (원)', kind: 'num', auto: amtOf, autoLabel: CO },
    { slot: 'shares', label: '취득 / 전환가능주식수', kind: 'num', auto: sharesOf, autoLabel: '계약금액 ÷ 전환가격' },
    { slot: 'contractNo', label: '계약번호 (저장 후 VICS 부여 · 1320에서 이 건 선택)', hint: '저장 후 표시되는 번호를 적어 두기' },
  ] },
  { key: '1320', title: '투자거래 관리', note: '「조회」 → 1319에서 만든 투자 건 클릭 → 「입력」 → 아래 값 입력(거래일자·등기변경일자 두 칸에 같은 날) → 첨부에 등기부등본·입금증빙 → 「저장」 · 고정 선택: 거래구분 투자 · 통화 대한민국 원', scope: 'co', docs: ['regcb', 'fundout'], fields: [
    // 거래일자(조합 → 법인 입금일)와 등기변경일자(사채 발행일자)는 실무상 같은 날 — 한 칸으로 합쳤다 (지도사 2026-09-24 "거래일자가 곧 등기변경일자로 실무상 사용해, 두개 합해도 돼")
    { slot: 'regDate', label: '거래일자 · 등기변경일자 (사채 발행일자 — 실무상 같은 날)', kind: 'date', auto: issueOf, autoLabel: REG, doc: 'regcb', hint: 'VICS 두 칸에 같은 날짜' },
    { slot: 'principal', label: '거래원금 (원화)', kind: 'num', auto: amtOf, autoLabel: CO },
    // 액면가·거래단가(전환가격)·총 발행주식수·거래주수 칸은 뺐다 — 전환사채 거래 입력에는 쓰지 않는 칸 (지도사 2026-09-24 "이 입력칸은 필요없다")
  ] },
  { key: '1329', title: '보고 검증 및 확정', note: '조합 단위로 한 번 — 모든 투자 법인의 1313·1319·1320 입력이 끝난 뒤 「검증」 → 오류 없으면 「확정/전송」 · 오류가 나면 「오류확인」으로 해당 화면(대개 1313 첨부 누락)을 고치고 다시 검증', docs: [], fields: [], scope: 'fund' },
];

/** 법인별 화면(1313·1319·1320)과 조합 단위 화면(1329) */
export const VICS_CO_STAGES: VicsStage[] = VICS_STAGES.filter((s) => s.scope === 'co');
export const VICS_FUND_STAGE: VicsStage = VICS_STAGES.filter((s) => s.scope === 'fund')[0];
export const VICS_FUND_KEY = 'vics_fund_1329';   // s13 chk — 조합 단위 확정/전송 완료
export const vicsFundDone = (p: Project): boolean => getChk(p, 's13', VICS_FUND_KEY);

export const vicsSlot = (coId: string, slot: string): string => 'vics_' + coId + '_' + slot;
export interface VicsVal { value: string; auto: string; manual: string; }
export function vicsVal(p: Project, co: CoCard, f: VicsField): VicsVal {
  const rc = regCoOf(p, co);
  const manual = getTxt(p, 's13', vicsSlot(co.id, f.slot));
  const auto = f.auto ? (f.auto(p, co, rc) || '') : '';
  return { value: manual || auto, auto, manual };
}
export const vicsDone = (p: Project, coId: string, stage: VicsStageKey): boolean => getChk(p, 's13', vicsSlot(coId, 'done_' + stage));

/** 첨부 서류 위치 — 앞단계 업로드(S3) 또는 1단계 연동(자료수집 파일명). 없으면 여기서 올려도 앞단계 키에 저장된다 */
/** srcKey: 앞단계 행에 「1단계 연동 / 직접 업로드」 선택이 있는 경우(8단계 사업자등록증) — 여기서 올리면 그 선택도 「직접 업로드」로 바꿔 앞단계 화면에 바로 보이게 한다 */
export interface VicsDoc { key: VicsDocKey; label: string; name: string; s3key: string; collect: boolean; store: StoreKey; slot: string; from: string; srcKey?: string; }
export function vicsDoc(p: Project, co: CoCard, key: VicsDocKey): VicsDoc {
  const pick = (store: StoreKey, slot: string) => ({ name: getUp(p, store, slot), s3key: getUpKey(p, store, slot) });
  if (key === 'regcb') { const u = pick('s9', co.id + '_regcb'); return { key, label: '법인등기부등본 (전환사채 등기 후)', ...u, collect: false, store: 's9', slot: co.id + '_regcb', from: '9단계' }; }
  if (key === 'vcert') { const u = pick('s11', co.id + '_vcert'); return { key, label: '벤처기업확인서', ...u, collect: false, store: 's11', slot: co.id + '_vcert', from: '12단계' }; }
  if (key === 'fundout') { const slot = kitKey(co.id, 'cb_fund', 'fundout'); const u = pick('s13', slot); return { key, label: '조합 → 법인 입금내역 (이체확인증 등)', ...u, collect: false, store: 's13', slot, from: '14단계 서류' }; }
  // 사업자등록증: 8단계 구비서류 행(직접 업로드) → (구) s9 저장분 → 1단계 자료수집 제출. 여기서 올리면 8단계 행에 저장된다 (2026-09-24 뒷단계 업로드 → 앞단계 연계)
  const u8 = pick('s8', co.id + '_biz'); const u9 = pick('s9', co.id + '_biz');
  const linked = p.collect ? coVal(p.collect, co, 'co_biz') : '';
  const s8src = (getStore(p, 's8').src || {})[co.id + '_biz'];
  if (u8.name && (s8src !== 'link' || !linked)) return { key, label: '법인 사업자등록증', ...u8, collect: false, store: 's8', slot: co.id + '_biz', from: '8단계', srcKey: co.id + '_biz' };
  if (u9.name) return { key, label: '법인 사업자등록증', ...u9, collect: false, store: 's9', slot: co.id + '_biz', from: '9단계(구)' };
  return { key, label: '법인 사업자등록증', name: linked, s3key: '', collect: !!linked, store: 's8', slot: co.id + '_biz', from: '1단계 연동', srcKey: co.id + '_biz' };
}

/** 준비도용 — 법인별 3화면 완료 여부 (조합 인수 전환사채 방식에서만 호출) · 조합 단위 1329는 readiness 가 따로 1건 센다 */
export function vicsChecklist(p: Project, co: CoCard): [string, boolean][] {
  const nm = co.name || '법인';
  return VICS_CO_STAGES.map((s) => [nm + '·VICS ' + s.key + ' ' + s.title, vicsDone(p, co.id, s.key)]);
}
export const vicsDoneCount = (p: Project, coId: string): number => VICS_CO_STAGES.filter((s) => vicsDone(p, coId, s.key)).length;

/** 화면 1개의 입력값을 「라벨: 값」 줄로 — 복사용 */
export function vicsStageText(p: Project, co: CoCard, stage: VicsStage): string {
  const lines = ['[VICS ' + stage.key + ' ' + stage.title + '] ' + (co.name || '')];
  stage.fields.forEach((f) => { const v = vicsVal(p, co, f); lines.push(f.label + ': ' + (v.value || '(미입력)')); });
  return lines.join('\n');
}

/* ── 서류 발급 사이트 · 서류 자동 인식 (2026-09-24 지도사: "자동으로 인식해서 채워놓던지 · 확인서 없으면 여기서 업로드 · 사이트 링크") ── */
export interface DocSite { label: string; url: string; hint: string; }
export const VICS_DOC_SITES: Partial<Record<VicsDocKey, DocSite>> = {
  regcb: { label: '인터넷등기소', url: 'https://www.iros.go.kr/', hint: '법인 등기사항증명서 열람·발급' },
  biz: { label: '홈택스', url: 'https://www.hometax.go.kr/', hint: '사업자등록증 재발급·출력' },
  vcert: { label: '벤처확인시스템', url: 'https://www.smes.go.kr/venturein/', hint: '벤처기업확인서 출력' },
};
export type ExtractDoc = 'regcb' | 'biz' | 'vcert';
export const EXTRACT_DOCS: ExtractDoc[] = ['regcb', 'biz', 'vcert'];
export type ExtractFields = Record<string, string>;
/** 인식된 값(백엔드 vicsParse 키) → VICS 슬롯. 'a+b' 는 두 값을 ' / ' 로 합친다 (업태 / 종목) */
export const EXTRACT_MAP: Record<ExtractDoc, [string, string][]> = {
  regcb: [['crno', 'crno'], ['founded', 'founded'], ['addr', 'addr'], ['ceoBirth', 'ceoBirth'], ['corp', 'corp'], ['cbIssueDate', 'regDate'], ['cbIssueDate', 'contractDate']],   // 총주식수·액면가·전환가는 1320 칸이 빠져 매핑도 뺐다
  biz: [['bizType+bizItem', 'biztype'], ['bizno', 'bizno']],
  vcert: [['vno', 'vno'], ['vfrom', 'vfrom'], ['vto', 'vto']],
};
export interface ExtractRow { slot: string; label: string; stage: VicsStageKey; value: string; current: string; }
export interface ExtractApply { patch: Partial<Project>; filled: ExtractRow[]; conflicts: ExtractRow[]; same: number; }

const fieldOf = (slot: string): { f: VicsField; stage: VicsStageKey } | null => {
  for (let i = 0; i < VICS_STAGES.length; i++) { const f = VICS_STAGES[i].fields.find((x) => x.slot === slot); if (f) return { f, stage: VICS_STAGES[i].key }; }
  return null;
};
const pick = (fields: ExtractFields, key: string): string => key.split('+').map((k) => (fields[k] || '').trim()).filter(Boolean).join(' / ');
/** 값의 출처 표식 — s13 src[vics_<co>_<slot>] = 'doc:regcb' 등 (사용자가 고쳐 쓰면 지운다) */
export const vicsSrc = (p: Project, coId: string, slot: string): string => { const st = getStore(p, 's13'); return (st.src && st.src[vicsSlot(coId, slot)]) || ''; };

/** 인식 결과를 빈 칸에 채운다. 이미 값(자동 포함)이 있고 다르면 conflicts 로 돌려주고, overwrite 에 든 슬롯만 덮어쓴다 */
export function applyExtract(p: Project, co: CoCard, doc: ExtractDoc, fields: ExtractFields, overwrite?: string[]): ExtractApply {
  const filled: ExtractRow[] = []; const conflicts: ExtractRow[] = []; let same = 0;
  const ow = overwrite || [];
  let cur: Project = p;
  EXTRACT_MAP[doc].forEach(([key, slot]) => {
    const value = pick(fields, key);
    if (!value) return;
    const def = fieldOf(slot); if (!def) return;
    const current = vicsVal(cur, co, def.f).value;
    const row: ExtractRow = { slot, label: def.f.label, stage: def.stage, value, current };
    // 숫자 칸은 콤마·공백 차이를 같은 값으로 본다 (등록정보 '60,000,000' vs 인식 '60000000')
    const eq = def.f.kind === 'num' ? current.replace(/[^0-9]/g, '') === value.replace(/[^0-9]/g, '') : current.trim() === value.trim();
    if (current && eq) { same += 1; return; }
    if (current && ow.indexOf(slot) < 0) { conflicts.push(row); return; }
    cur = { ...cur, ...mutStore(cur, 's13', (st) => { st.txt = st.txt || {}; st.src = st.src || {}; st.txt[vicsSlot(co.id, slot)] = value; st.src[vicsSlot(co.id, slot)] = 'doc:' + doc; }) };
    filled.push(row);
  });
  return { patch: cur === p ? {} : { s13: cur.s13 }, filled, conflicts, same };
}

/** 9단계 「벤처기업 확인시스템 ID·PW」(1단계 연동 또는 직접 입력) — 벤처확인시스템에서 확인서를 내려받을 때 쓴다 (2026-09-24)
 *  한 칸에 "kotec / 1234" · "ID: kotec, PW: 1234" · "아이디 kotec 비밀번호 1234" 처럼 적혀 있어 ID·PW 를 나눠 본다 */
export interface SiteCred { text: string; id: string; pw: string; from: string; }
export function vcertCred(p: Project, co: CoCard): SiteCred {
  const key = co.id + '_idpw'; const st = getStore(p, 's9');
  const linked = p.collect ? coVal(p.collect, co, 'co_idpw') : '';
  const src = (st.src && st.src[key]) || (linked ? 'link' : 'upload');
  const text = (src === 'link' ? linked : getTxt(p, 's9', key)).trim();
  const from = text ? (src === 'link' ? '1단계 연동' : '9단계 입력') : '';
  const strip = (v: string): string => v.replace(/^(?:id|pw|아이디|비밀번호|비번|password|패스워드)\s*[:：=]?\s*/i, '').trim();
  let parts = text.split(/\s*[/,|\n]\s*/).map(strip).filter(Boolean);
  if (parts.length < 2) { const m = text.match(/^(?:id|아이디)?\s*[:：]?\s*(\S+)\s+(?:pw|비밀번호|비번|password)\s*[:：]?\s*(\S+)$/i); if (m) parts = [m[1], m[2]]; }
  return parts.length >= 2 ? { text, id: parts[0], pw: parts[1], from } : { text, id: '', pw: '', from };
}
